//@ts-nocheck

const UNLIMITED_PLAN_LIMIT = 30;

export const getItemDepositAmount = (item) => {
  const depositamount = Number(item?.depositamount);
  const selectedQuantity = Number(item?.selectedQuantity);

  if (!isNaN(depositamount) && !isNaN(selectedQuantity)) {
    return depositamount * selectedQuantity;
  } else {
    return 0;
  }
};

export const getItemTotal = (item) => {
  const priceperperson = Number(item?.priceperperson);
  const selectedQuantity = Number(item?.selectedQuantity);

  if (!isNaN(priceperperson) && !isNaN(selectedQuantity)) {
    return priceperperson * selectedQuantity;
  } else {
    return 0;
  }
};

/**
 * Gets multiples of a number
 * @param {number} num - The number of passengers per plan
 * @param {number} limit - The maximum available number of passengers
 * @returns {number[]} - Array of the available number of passengers to build dropdown options
 */
export const getNumberMultiples = (num, limit) => {
  const multiples = [];

  if (num <= 0 || limit < 0) {
    return [0];
  }

  // Loop through numbers from 0 to the limit, checking for multiples
  for (let i = 0; i <= limit; i++) {
    if (i % num === 0) {
      multiples.push(i);
    }
  }

  return multiples;
};

/**
 * Checks if a plan has limited availability
 * @param {Object} plan - The booking plan to check
 * @returns {boolean} - True if the plan has limited availability, false otherwise
 */
export const planHasLimitedAvailability = (plan) => {
  return (
    !isNaN(parseInt(plan?.maxavailable)) && parseInt(plan?.maxavailable) > 0
  );
};

/**
 * Checks if a plan is sold out
 * @param {Object} plan - The booking plan to check
 * @returns {boolean} - True if the plan has limited availability and is sold out, false otherwise
 */
export const planIsSoldOut = (plan) => {
  return planHasLimitedAvailability(plan) && plan.available <= 0;
};

/**
 * Checks if a plan is past its booking deadline
 * @param {Object} plan - The booking plan to check
 * @returns {boolean} - True if the plan is past its booking deadline, false otherwise
 */
export const planIsPastDeadline = (plan) => {
  // Make sure users can book until END OF DAY (23:59:59) on the lastbookdate
  if (plan.lastbookdate === null || plan.lastbookdate == '') {
    return false;
  }

  // Create a date object from the lastbookdate
  const lastBookDate = new Date(plan.lastbookdate);

  // Set the time to 23:59:59 (end of day)
  const endOfDay = new Date(
    lastBookDate.getUTCFullYear(),
    lastBookDate.getUTCMonth(),
    lastBookDate.getUTCDate(),
    23,
    59,
    59,
    999
  );

  // Compare with current date
  return new Date() > endOfDay;
};

export const getBookingPlanDropdownOptions = (plan) => {
  // Max plans that can be booked is:
  //  0 if past deadline or sold out
  //  plan.available if it has limited availability
  //  30 if it has unlimited availability
  let maxPlansBookable = UNLIMITED_PLAN_LIMIT;
  if (planIsPastDeadline(plan) || planIsSoldOut(plan)) {
    maxPlansBookable = 0;
  } else if (planHasLimitedAvailability(plan)) {
    maxPlansBookable = parseInt(plan?.available);
  } else {
    maxPlansBookable = UNLIMITED_PLAN_LIMIT;
  }

  const planOccupancy = parseInt(plan?.occupancy);

  // Note available and maxPlanAvailable are number of PLANS, not passengers
  //  We need to multiply by occupancy to build the dropdown options
  if (!isNaN(maxPlansBookable) && !isNaN(planOccupancy)) {
    const options = getNumberMultiples(
      planOccupancy,
      maxPlansBookable * planOccupancy
    );

    return options.map((option) => {
      return {
        id: option.toString(),
        value: option.toString(),
        label: option.toString()
      };
    });
  }

  return [
    {
      id: '0',
      value: '0',
      label: '0'
    }
  ];
};

export const getPlanDepositAmount = (plan) => {
  const depositamount = Number(plan?.depositamount);
  const occupancy = Number(plan?.occupancy);

  if (!isNaN(depositamount) && !isNaN(occupancy)) {
    return depositamount * occupancy;
  } else {
    return 0;
  }
};

export const getPlanTotal = (plan) => {
  const priceperperson = Number(plan?.priceperperson);

  if (!isNaN(priceperperson)) {
    return priceperperson;
  } else {
    return 0;
  }
};

export const hasMixedPlans = (bookingPlans = []) => {
  const plansWithDeposit = bookingPlans?.filter(
    (plan) => plan?.depositamount > 0 && plan?.selectedQuantity > 0
  );

  const plansWithoutDeposit = bookingPlans?.filter(
    (plan) => !plan?.depositamount && plan?.selectedQuantity > 0
  );

  return plansWithDeposit.length > 0 && plansWithoutDeposit.length > 0;
};

export const getBookingTotals = (bookingPlans = []) => {
  let totalPrice = 0;
  let totalDeposit = 0;
  const selectedPlans = bookingPlans?.filter(
    (plan) => plan.selectedQuantity > 0
  );
  const includeMixedPlans = hasMixedPlans(selectedPlans);

  selectedPlans.forEach((tripPlan) => {
    const selectedQuantity = Number(tripPlan?.selectedQuantity);
    const priceperperson = Number(tripPlan?.priceperperson);
    const depositamount = Number(tripPlan?.depositamount);

    //Get pricing plan total
    if (!isNaN(priceperperson)) {
      totalPrice += priceperperson * selectedQuantity;
    }

    //Get pricing plan deposit amount
    if (includeMixedPlans) {
      if (!isNaN(depositamount) && depositamount > 0) {
        totalDeposit += depositamount * selectedQuantity;
      } else {
        totalDeposit += priceperperson * selectedQuantity;
      }
    } else {
      if (!isNaN(depositamount)) {
        totalDeposit += depositamount * selectedQuantity;
      }
    }
  });

  return {
    totalPrice,
    totalDeposit
  };
};

export const getPassengerName = (passenger) => {
  const firstName =
    passenger?.passengerfields?.find((field) => field?.title === 'First Name')
      ?.options[0]?.value || '';
  const lastName =
    passenger?.passengerfields?.find((field) => field?.title === 'Last Name')
      ?.options[0]?.value || '';
  return `${firstName} ${lastName}`;
};
